import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { orderService } from '../services/orderService';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Tracking = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (orderId) {
      fetchTrackingDetails();
    }
  }, [orderId, user]);

  const fetchTrackingDetails = async () => {
    try {
      setLoading(true);
      const response = await orderService.getTrackingDetails(orderId);
      if (response.success) {
        setOrder(response.order);
        setTrackingInfo(response.trackingInfo);
        
        // Vérifier que la commande appartient à l'utilisateur
        const userId = user?.id || localStorage.getItem('userId');
        const ownerUid = response.order.userUid || response.order.userId;
        if (ownerUid !== userId && user?.role !== 'agent' && user?.role !== 'admin') {
          setError('Vous n\'avez pas accès à cette commande');
          setLoading(false);
          return;
        }
      } else {
        setError(response.error || 'Erreur lors de la récupération des détails');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError('Une erreur est survenue lors du chargement des informations');
    } finally {
      setLoading(false);
    }
  };

  const getStatusSteps = () => {
    const steps = [
      { key: 'Non expédié', label: 'Non expédié', icon: '📦', color: '#999', description: 'Commande en attente d\'expédition' },
      { key: 'En transit', label: 'En transit', icon: '🚚', color: '#007bff', description: 'Votre colis est en route' },
      { key: 'Arrivé en RDC', label: 'Arrivé en RDC', icon: '🇨🇩', color: '#28a745', description: 'Votre colis est arrivé en RDC' },
      { key: 'Prêt à être livré', label: 'Prêt à être livré', icon: '🏠', color: '#ffc107', description: 'Votre colis est prêt pour la livraison' },
      { key: 'Livré', label: 'Livré', icon: '✅', color: '#28a745', description: 'Votre commande a été livrée' },
    ];

    const currentIndex = steps.findIndex(step => step.key === order?.deliveryStatus);
    
    return steps.map((step, index) => ({
      ...step,
      completed: index <= currentIndex && currentIndex >= 0,
      current: index === currentIndex,
    }));
  };

  const getOrderStatusSteps = () => {
    const steps = [
      { key: 'Commandée', label: 'Commandée', icon: '📝', color: '#FF0000', description: 'Votre commande a été reçue' },
      { key: 'Payée', label: 'Payée', icon: '💳', color: '#28a745', description: 'Paiement confirmé' },
      { key: 'Expédiée', label: 'Expédiée', icon: '📦', color: '#007bff', description: 'Commande expédiée' },
      { key: 'Livrée', label: 'Livrée', icon: '✅', color: '#28a745', description: 'Commande livrée' },
    ];

    const currentIndex = steps.findIndex(step => step.key === order?.status);
    
    return steps.map((step, index) => ({
      ...step,
      completed: index <= currentIndex && currentIndex >= 0,
      current: index === currentIndex,
    }));
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="container">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
            <p style={{ fontSize: '18px', color: '#666' }}>Chargement des informations de suivi...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div>
        <Navbar />
        <div className="container">
          <div style={{ textAlign: 'center', padding: '60px 20px', maxWidth: '600px', margin: '0 auto' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
            <h2 style={{ color: '#dc3545', marginBottom: '20px' }}>Erreur</h2>
            <p style={{ color: '#666', marginBottom: '30px', fontSize: '16px' }}>
              {error || 'Commande non trouvée'}
            </p>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/dashboard')}
            >
              Retour au dashboard
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const statusSteps = getStatusSteps();
  const orderStatusSteps = getOrderStatusSteps();
  
  // Vérifier si c'est une commande avec plusieurs produits
  const hasMultipleProducts = order.items && Array.isArray(order.items) && order.items.length > 0;

  return (
    <div>
      <Navbar />

      <div className="container">
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h1 className="dashboard-title" style={{ textAlign: 'center', marginBottom: '30px' }}>
            📦 Suivi de livraison
          </h1>

          {/* Informations de la commande */}
          <div className="card" style={{ marginBottom: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
              <div>
                <h3 style={{ marginBottom: '10px', color: '#FF0000', fontSize: '24px' }}>
                  Commande #{order._id.slice(-8).toUpperCase()}
                  {hasMultipleProducts && (
                    <span style={{ fontSize: '16px', color: '#666', fontWeight: 'normal', marginLeft: '10px' }}>
                      ({order.items.length} produit{order.items.length > 1 ? 's' : ''})
                    </span>
                  )}
                </h3>
                <p style={{ color: '#666', marginBottom: '8px', fontSize: '14px' }}>
                  <strong>Plateforme:</strong> {order.platform}
                </p>
                <p style={{ color: '#666', marginBottom: '8px', fontSize: '14px' }}>
                  <strong>Date de commande:</strong> {formatDate(order.createdAt)}
                </p>
                <p style={{ color: '#666', marginBottom: '8px', fontSize: '14px' }}>
                  <strong>Statut:</strong>{' '}
                  <span style={{ 
                    color: order.status === 'Livrée' ? '#28a745' : order.status === 'Payée' ? '#28a745' : order.status === 'Expédiée' ? '#007bff' : '#FF0000',
                    fontWeight: 'bold'
                  }}>
                    {order.status}
                  </span>
                </p>
                <p style={{ color: '#666', marginBottom: '8px', fontSize: '14px' }}>
                  <strong>Paiement:</strong>{' '}
                  <span style={{ 
                    color: order.paymentStatus === 'Payé' ? '#28a745' : '#666',
                    fontWeight: order.paymentStatus === 'Payé' ? 'bold' : 'normal'
                  }}>
                    {order.paymentStatus}
                  </span>
                </p>
              </div>
              {order.totalAmount && (
                <div style={{
                  padding: '15px',
                  backgroundColor: '#f0f9ff',
                  borderRadius: '8px',
                  border: '1px solid #bae6fd',
                  minWidth: '200px',
                }}>
                  <p style={{ margin: '5px 0', fontSize: '14px', color: '#0369a1' }}>
                    <strong>Montant total:</strong>
                  </p>
                  <p style={{ margin: '5px 0', fontSize: '24px', fontWeight: 'bold', color: '#FF0000' }}>
                    ${order.totalAmount.toFixed(2)}
                  </p>
                </div>
              )}
            </div>

            {/* Liste des produits */}
            {hasMultipleProducts ? (
              <div style={{ marginTop: '20px' }}>
                <h4 style={{ marginBottom: '15px', color: '#333', fontSize: '18px' }}>Produits commandés</h4>
                <div style={{ display: 'grid', gap: '15px' }}>
                  {order.items.map((item, index) => (
                    <div key={index} style={{
                      padding: '15px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e8e8e8',
                    }}>
                      <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                        {item.imageUrl && (
                          <img
                            src={item.imageUrl}
                            alt={`Produit ${index + 1}`}
                            style={{
                              width: '80px',
                              height: '80px',
                              borderRadius: '8px',
                              border: '1px solid #ddd',
                              objectFit: 'cover',
                            }}
                          />
                        )}
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: '#333' }}>
                            Produit {index + 1}
                            {item.verifiedProductName && (
                              <span style={{ fontWeight: 'normal', color: '#666', marginLeft: '8px' }}>
                                - {item.verifiedProductName}
                              </span>
                            )}
                          </p>
                          <p style={{ margin: '3px 0', fontSize: '13px', color: '#666' }}>
                            <strong>Quantité:</strong> {item.quantity}
                            {item.size && <span> | <strong>Taille:</strong> {item.size}</span>}
                            {item.color && <span> | <strong>Couleur:</strong> {item.color}</span>}
                          </p>
                          {item.productLink && (
                            <a
                              href={item.productLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: '#FF0000', textDecoration: 'none', fontSize: '12px', fontWeight: '500' }}
                            >
                              Voir le produit →
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {order.verifiedProductName && (
                  <p style={{ color: '#666', marginBottom: '10px' }}>
                    <strong>Produit:</strong> {order.verifiedProductName}
                  </p>
                )}
                {order.productLink && (
                  <p style={{ color: '#666', marginBottom: '10px' }}>
                    <strong>Lien:</strong>{' '}
                    <a href={order.productLink} target="_blank" rel="noopener noreferrer" style={{ color: '#FF0000' }}>
                      Voir le produit
                    </a>
                  </p>
                )}
                {order.quantity && (
                  <p style={{ color: '#666', marginBottom: '10px' }}>
                    <strong>Quantité:</strong> {order.quantity}
                  </p>
                )}
              </>
            )}

            {order.trackingId && (
              <div style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #bae6fd',
              }}>
                <p style={{ margin: '5px 0', fontSize: '18px', fontWeight: 'bold', color: '#0369a1' }}>
                  🏷️ Tracking ID: <span style={{ fontFamily: 'monospace', fontSize: '20px' }}>{order.trackingId}</span>
                </p>
                <p style={{ margin: '5px 0', fontSize: '14px', color: '#0369a1' }}>
                  Utilisez ce numéro pour suivre votre colis
                </p>
              </div>
            )}
          </div>

          {/* Timeline du statut de commande */}
          <div className="card" style={{ marginBottom: '30px' }}>
            <h3 style={{ marginBottom: '20px', color: '#333' }}>Statut de la commande</h3>
            
            <div style={{ position: 'relative', paddingLeft: '30px' }}>
              <div style={{
                position: 'absolute',
                left: '15px',
                top: '0',
                bottom: '0',
                width: '2px',
                backgroundColor: '#e0e0e0',
              }} />

              {orderStatusSteps.map((step, index) => (
                <div
                  key={step.key}
                  style={{
                    position: 'relative',
                    marginBottom: index < orderStatusSteps.length - 1 ? '30px' : '0',
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    left: '-22px',
                    top: '0',
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    backgroundColor: step.completed ? step.color : '#e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    border: '3px solid white',
                    boxShadow: step.current ? `0 0 0 3px ${step.color}40` : 'none',
                    zIndex: 1,
                  }}>
                    {step.completed ? step.icon : '○'}
                  </div>

                  <div style={{
                    paddingLeft: '20px',
                    opacity: step.completed ? 1 : 0.6,
                  }}>
                    <h4 style={{
                      margin: '0 0 5px 0',
                      color: step.completed ? step.color : '#999',
                      fontWeight: step.current ? 'bold' : 'normal',
                    }}>
                      {step.label}
                    </h4>
                    <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Timeline de livraison */}
          {order.trackingId && (
            <div className="card" style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#333' }}>Suivi de livraison</h3>
              
              <div style={{ position: 'relative', paddingLeft: '30px' }}>
                <div style={{
                  position: 'absolute',
                  left: '15px',
                  top: '0',
                  bottom: '0',
                  width: '2px',
                  backgroundColor: '#e0e0e0',
                }} />

                {statusSteps.map((step, index) => (
                  <div
                    key={step.key}
                    style={{
                      position: 'relative',
                      marginBottom: index < statusSteps.length - 1 ? '30px' : '0',
                    }}
                  >
                    <div style={{
                      position: 'absolute',
                      left: '-22px',
                      top: '0',
                      width: '30px',
                      height: '30px',
                      borderRadius: '50%',
                      backgroundColor: step.completed ? step.color : '#e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      border: '3px solid white',
                      boxShadow: step.current ? `0 0 0 3px ${step.color}40` : 'none',
                      zIndex: 1,
                    }}>
                      {step.completed ? step.icon : '○'}
                    </div>

                    <div style={{
                      paddingLeft: '20px',
                      opacity: step.completed ? 1 : 0.6,
                    }}>
                      <h4 style={{
                        margin: '0 0 5px 0',
                        color: step.completed ? step.color : '#999',
                        fontWeight: step.current ? 'bold' : 'normal',
                      }}>
                        {step.label}
                      </h4>
                      <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                        {step.description}
                      </p>
                      {step.current && (
                        <p style={{ margin: '5px 0 0 0', color: step.color, fontSize: '13px', fontWeight: 'bold' }}>
                          ⬅ Statut actuel
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Informations de tracking détaillées */}
          {trackingInfo && trackingInfo.success && (
            <div className="card" style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', color: '#333' }}>Détails du suivi</h3>
              
              {trackingInfo.location && (
                <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                  <p style={{ margin: '0', fontSize: '14px', color: '#0369a1' }}>
                    <strong>📍 Localisation actuelle:</strong> {trackingInfo.location}
                  </p>
                </div>
              )}
              
              {trackingInfo.estimatedDelivery && (
                <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
                  <p style={{ margin: '0', fontSize: '14px', color: '#856404' }}>
                    <strong>📅 Livraison estimée:</strong> {formatDate(trackingInfo.estimatedDelivery)}
                  </p>
                </div>
              )}

              {trackingInfo.events && trackingInfo.events.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <h4 style={{ marginBottom: '15px', color: '#333' }}>Historique des événements</h4>
                  {trackingInfo.events.map((event, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '15px',
                        marginBottom: '10px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        borderLeft: '4px solid #007bff',
                      }}
                    >
                      <p style={{ margin: '0 0 5px 0', fontWeight: 'bold', color: '#333' }}>
                        {formatDate(event.date)}
                      </p>
                      {event.location && (
                        <p style={{ margin: '0 0 5px 0', color: '#666', fontSize: '14px' }}>
                          📍 {event.location}
                        </p>
                      )}
                      {event.description && (
                        <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                          {event.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Message si pas de tracking ID */}
          {!order.trackingId && (
            <div className="card" style={{ marginBottom: '30px', backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div style={{ fontSize: '32px' }}>⏳</div>
                <div>
                  <h4 style={{ margin: '0 0 5px 0', color: '#856404' }}>En attente d'expédition</h4>
                  <p style={{ margin: '0', color: '#856404', fontSize: '14px' }}>
                    Votre commande sera expédiée une fois le paiement confirmé. Un numéro de suivi vous sera communiqué dès l'expédition.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Adresse de livraison */}
          {order.deliveryAddress && (
            <div className="card" style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '15px', color: '#333' }}>📍 Adresse de livraison</h3>
              <p style={{ color: '#666', margin: '0' }}>{order.deliveryAddress}</p>
            </div>
          )}

          {/* Bouton retour */}
          <div style={{ textAlign: 'center', marginTop: '30px' }}>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/dashboard')}
            >
              Retour au dashboard
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Tracking;

